import axios from "axios";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import { Skeleton } from "@chakra-ui/react";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import Plus from "../../assets/svgs/Plus.svg";
import AddLeadRoundRobinModal from "./modals/LeadRoundRobinModals/AddLeadRoundRobinModal";

const LeadRoundRobin = () => {
    const [config] = useAuth();
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [leadsData, setLeadsData] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemPerPage, setItemPerPage] = useState(10);
    const [selectedItem, setSelectedItem] = useState([]);
    const [isSelectAll, setIsSelectAll] = useState(false);
    const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
    const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });
    const [sortBy, setSortBy] = useState("name");
    const [sortDirection, setSortDirection] = useState("asc");

    // Drag & Drop State
    const [draggedIndex, setDraggedIndex] = useState(null);

    // ====================== DRAG & DROP HANDLERS ======================
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();
        const dragIndex = draggedIndex;

        if (dragIndex === null || dragIndex === dropIndex) {
            setDraggedIndex(null);
            return;
        }

        const draggedItem = leadsData[dragIndex];
        const newLeadsData = [...leadsData];

        // Optimistic reordering
        newLeadsData.splice(dragIndex, 1);
        newLeadsData.splice(dropIndex, 0, draggedItem);

        setLeadsData(newLeadsData);

        // Calculate new position (1-based)
        const newPosition = dropIndex + 1;

        try {
            await axios.post(
                `${BASE_URL}/lead-round-robin-update-position`,
                {
                    user_id: draggedItem.user_id,      // From API response
                    position: newPosition,
                    is_active: draggedItem.is_active ?? true,
                },
                config
            );

            console.log(`Position updated successfully for user_id: ${draggedItem.user_id}`);
            
            // Refresh data from backend
            fetchData();
        } catch (err) {
            console.error("Failed to update position:", err);
            // Revert on error
            fetchData();
        }

        setDraggedIndex(null);
    };

    // ====================== EXISTING FUNCTIONS ======================
    const handleSelectCheck = (id) => {
        if (selectedItem.length === 0) {
            setSelectedItem([id]);
        } else {
            const index = selectedItem.indexOf(id);
            if (index === -1) {
                setSelectedItem([...selectedItem, id]);
            } else {
                setSelectedItem(selectedItem.filter((el) => el !== id));
            }
        }
    };

    const handleNext = () => {
        if (currentPage !== paginationData.totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePrev = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const fetchData = () => {
        setLoading(true);
        axios
            .get(
                `${BASE_URL}/lead-round-robin-list?page=${currentPage}&per_page=${itemPerPage}&search=${search}&sort_direction=${sortDirection}&sort_by=${sortBy}`,
                config
            )
            .then((res) => {
                const leasetypeList = res?.data?.data?.order || [];
                setLeadsData(leasetypeList);

                setPaginationData({
                    totalItems: leasetypeList.length,
                    from: leasetypeList.length ? 1 : 0,
                    to: leasetypeList.length,
                    totalPages: 1,
                });

                setSelectedItem([]);
                setIsSelectAll(false);
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
                setSelectedId("");
            });
    };

    useEffect(() => {
        fetchData();
    }, [sortBy, sortDirection]);

    useEffect(() => {
        fetchData();
    }, [currentPage, itemPerPage, search]);

    return (
        <BaseLayout>
            <div className="contact-header">
                <p className="head-1 dark-H">
                    Lead Round Robin
                    {selectedItem?.length > 0 && (
                        <span className="body-L ml-2">
                            ({selectedItem?.length} <span className="dark-H">{selectedItem?.length === 1 ? "item" : "items"} selected</span>)
                        </span>
                    )}
                </p>
                <div className="md:flex block gap-6 items-center">
                    <div className="flex gap-6 justify-between items-center">
                        <div className="search-box contacts">
                            <input
                                type="text"
                                className="body-S"
                                placeholder="search name here..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{ border: "1px solid #D8D8D8" }}
                            />
                        </div>
                        <button
                            className="add-contact-button green-bg-H light-L body-S"
                            onClick={() => {
                                setSelectedId("");
                                setShowModal(true);
                            }}
                        >
                            <img className="mr-2 sidebar-icons" src={Plus} alt="plus" /> Add
                        </button>
                    </div>
                </div>
            </div>

            <div className="light-bg-L pb-3 table-container">
                <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
                        <table className="contact-table two-rows-static light-bg-L">
                            <thead>
                                <tr className="uppercase body-N dark-M">
                                    <th className="green-H">
                                        <div className="table-header">
                                            Name
                                            <img
                                                role="button"
                                                src={sortBy !== "name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                                                onClick={() => {
                                                    setSortBy("name");
                                                    setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                                                }}
                                                alt="sort icon"
                                            />
                                        </div>
                                    </th>
                                    <th>
                                        <div className="table-header">
                                            Source
                                            <img
                                                role="button"
                                                src={sortBy !== "source" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                                                onClick={() => {
                                                    setSortBy("source");
                                                    setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                                                }}
                                                alt="sort icon"
                                            />
                                        </div>
                                    </th>
                                    <th>
                                        <div className="table-header">
                                            Next Up
                                            <img
                                                role="button"
                                                src={sortBy !== "is_next_up" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                                                onClick={() => {
                                                    setSortBy("is_next_up");
                                                    setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                                                }}
                                                alt="sort icon"
                                            />
                                        </div>
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    [...Array(10)].map((_, idx) => (
                                        <tr key={idx} className="dark-H body-N">
                                            <td><Skeleton height="20px" width="140px" /></td>
                                            <td><Skeleton height="20px" width="140px" /></td>
                                            <td><Skeleton height="20px" width="80px" /></td>
                                        </tr>
                                    ))
                                ) : leadsData?.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="body-N text-center mt-6 p-10 border-0">
                                            No Leads Found
                                        </td>
                                    </tr>
                                ) : (
                                    leadsData?.map((el, idx) => (
                                        <tr
                                        key={el.user_id || idx}
                                        className={`dark-H body-N ${draggedIndex === idx ? "opacity-50" : ""}`}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, idx)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, idx)}
                                        onDragEnd={handleDragEnd}
                                        style={{ cursor: "move" }}
                                    >
                                        <td className="capitalize">{el?.name}</td>
                                        <td className="capitalize">{el?.source}</td>
                                        <td>{el?.is_active ? "Active" : "Inactive"}</td>
                                    </tr>
                                ))
                                )}
                            </tbody>
                        </table>
                </div>

                <ContactPagination
                    from="archive"
                    setSelectedItem={setSelectedItem}
                    selectedItem={selectedItem}
                    paginationData={paginationData}
                    handleNext={handleNext}
                    handlePrev={handlePrev}
                    onSuccess={() => fetchData()}
                    itemPerPage={itemPerPage}
                    handleItemPerPageSelect={(value) => {
                        setItemPerPage(value);
                        setCurrentPage(1);
                    }}
                />

                <AddLeadRoundRobinModal
                    showModal={showModal}
                    onClose={() => {
                        setShowModal(false);
                        setSelectedId("");
                    }}
                    onCallApi={fetchData}
                    totalItems={leadsData?.length || 0}
                />
            </div>
        </BaseLayout>
    );
};

export default LeadRoundRobin;